/**
 * Deep links into Info → Node Settings, focused on one machine's settings.
 *
 * The Live tab shows a machine's health status only once someone has entered a
 * rated power for it, and the Insights tab prices a loss from the machine's
 * worth-of-equipment / production-value / maintenance-cost figures. Both are
 * edited in one place (Node Settings), so those screens link here instead of
 * telling the user to go find the form.
 *
 * Machine names are not unique — `client_machines` happily holds three rows
 * called "Floor 1" on different meters — so the link carries `meter` when the
 * caller knows it and falls back to `machine` (the name) for callers like the
 * Insights API that only report a name.
 */

/** Node Settings is a section of the Info page, not its own route. */
export const NODE_SETTINGS_SECTION = "machines";

/** Values for the `focus` param — which field the landing page should flash. */
export const FOCUS_RATED_POWER = "rated_power";
export const FOCUS_LOSS_PARAMS = "loss_params";

export function buildNodeSettingsLink({ meterId, machineName, focus } = {}) {
  const params = new URLSearchParams({ section: NODE_SETTINGS_SECTION });
  if (meterId) params.set("meter", String(meterId));
  if (machineName) params.set("machine", String(machineName));
  if (focus) params.set("focus", focus);
  return `/info?${params.toString()}`;
}

/**
 * Should this row/node be offered the "assign rated power" link?
 *
 * Health status is live power as a percentage of rated power, so anything
 * without a rated power shows no health. Only some of those are fixable:
 *
 *  - virtual / grouping nodes aggregate their children and have no rated power
 *    of their own,
 *  - per-phase rows are a breakdown of the machine row above them, so the link
 *    belongs on the parent, not repeated three times underneath it.
 *
 * Callers map their own field names onto this shape.
 */
export function needsRatedPowerAssignment({
  isVirtual = false,
  isPhaseRow = false,
  ratedPower = null,
} = {}) {
  if (isVirtual || isPhaseRow) return false;
  return ratedPower === null || ratedPower === undefined;
}

/**
 * Does this Node Settings card correspond to the machine the link asked for?
 * Meter id wins; the name is only consulted when no meter was supplied.
 */
export function matchesFocusTarget(machine, target) {
  if (!machine || !target) return false;
  if (target.meterId) {
    return String(machine.meter_id || "").trim() === String(target.meterId).trim();
  }
  if (target.machineName) {
    return (
      String(machine.machine_name || "").trim().toLowerCase() ===
      String(target.machineName).trim().toLowerCase()
    );
  }
  return false;
}
